import React, { useEffect, useRef } from 'react';

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  hue: number;
};

const MouseParticles: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const particlesRef = useRef<Particle[]>([]);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return () => undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return () => undefined;

    const getHslVar = (name: string, fallback: string) => {
      const value = getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim();
      return value.length ? value : fallback;
    };

    // cores do tema (format: "190 90% 50%")
    const SKY = getHslVar('--sky', '190 90% 50%');
    const PRIMARY = getHslVar('--primary', '25 95% 53%');
    const WARM = getHslVar('--pink', '330 85% 60%');

    // extrai o H (hue) do hsl vars: "190 90% 50%" -> 190
    const hueFrom = (hslVar: string) => {
      const h = Number(String(hslVar).split(' ')[0]);
      return Number.isFinite(h) ? h : 190;
    };

    const palette = [hueFrom(SKY), hueFrom(PRIMARY), hueFrom(WARM)];

    const getThemeMode = () =>
      document.body.getAttribute('data-theme') === 'light' ? 'light' : 'dark';

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const onMove = (e: MouseEvent) => {
      // spawn
      if (Math.random() < 0.3) {
        const hue = palette[Math.floor(Math.random() * palette.length)];

        particlesRef.current.push({
          x: e.clientX + (Math.random() - 0.5) * 10,
          y: e.clientY + (Math.random() - 0.5) * 10,
          vx: (Math.random() - 0.5) * 0.9,
          vy: (Math.random() - 0.5) * 0.9,
          life: 0,
          maxLife: 44 + Math.random() * 26,
          size: 1.2 + Math.random() * 2.2,
          hue,
        });
      }

      // cap
      if (particlesRef.current.length > 120) {
        particlesRef.current.splice(0, particlesRef.current.length - 120);
      }
    };

    const onLeave = () => {
      // não precisa fazer nada, só para de gerar
    };

    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('mouseleave', onLeave);

    const drawParticle = (p: Particle) => {
      const t = 1 - p.life / p.maxLife; // 1 -> 0
      const alpha = Math.max(0, Math.min(1, t));

      const { x } = p;
      const { y } = p;

      // Cores (neon + glow) no estilo Lovable
      // ring: saturado, brilho maior; core: mais suave
      const isLight = getThemeMode() === 'light';

      const ring = isLight
        ? `hsla(${p.hue}, 88%, 46%, ${alpha * 0.82})`
        : `hsla(${p.hue}, 95%, 62%, ${alpha * 0.9})`;

      const core = isLight
        ? `hsla(${p.hue}, 92%, 44%, ${alpha * 0.28})`
        : `hsla(${p.hue}, 95%, 62%, ${alpha * 0.22})`;

      const glow = isLight
        ? `hsla(${p.hue}, 85%, 42%, ${alpha * 0.16})`
        : `hsla(${p.hue}, 95%, 62%, ${alpha * 0.12})`;

      // 1) glow grande
      ctx.beginPath();
      ctx.arc(x, y, p.size * 2.4, 0, Math.PI * 2);
      ctx.fillStyle = glow;
      ctx.fill();

      // 2) anel neon (stroke)
      ctx.beginPath();
      ctx.arc(x, y, p.size * 1.8, 0, Math.PI * 2);
      ctx.strokeStyle = ring;
      ctx.lineWidth = 1.25;
      ctx.stroke();

      // 3) miolo suave
      ctx.beginPath();
      ctx.arc(x, y, p.size * 0.95, 0, Math.PI * 2);
      ctx.fillStyle = core;
      ctx.fill();
    };

    const tick = () => {
      if (particlesRef.current.length === 0) {
        rafRef.current = null;
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const next: Particle[] = [];

      for (let i = 0; i < particlesRef.current.length; i += 1) {
        const p = particlesRef.current[i];

        const life = p.life + 1;
        if (life < p.maxLife) {
          const np: Particle = {
            ...p,
            x: p.x + p.vx,
            y: p.y + p.vy,
            life,
            vx: p.vx * 0.985,
            vy: p.vy * 0.985,
          };

          drawParticle(np);
          next.push(np);
        }
      }

      particlesRef.current = next;
      rafRef.current = requestAnimationFrame(tick);
    };

    const onMoveWithRaf = (e: MouseEvent) => {
      onMove(e);
      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    window.addEventListener('mousemove', onMoveWithRaf);

    rafRef.current = null;

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMoveWithRaf);
      window.removeEventListener('mouseleave', onLeave);

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      particlesRef.current = [];
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
        opacity:
          document.body.getAttribute('data-theme') === 'light' ? 0.78 : 0.95,
        mixBlendMode:
          document.body.getAttribute('data-theme') === 'light'
            ? 'multiply'
            : 'screen',
      }}
    />
  );
};

export default MouseParticles;
